<template>
	<ImageCard
		v-if="flat"
		:title="downloadName"
		:src="url"
		class="h-screen"
		close-btn
		save-btn
		@save="save"
		@close="$emit('close')"
	>
		<template v-if="$slots.prepend" #prepend>
			<slot name="prepend"></slot>
		</template>
	</ImageCard>

	<v-dialog
		v-if="!flat"
		v-model="toggle"
		fullscreen
		:scrim="false"
		transition="dialog-bottom-transition"
		@keydown="onKeydown"
	>
		<ImageCard
			:title="downloadName"
			:src="url"
			close-btn
			save-btn
			height="90vh"
			@save="save"
			@close="toggle = false"
		>
			<template v-if="$slots.prepend" #prepend>
				<slot name="prepend"></slot>
			</template>
		</ImageCard>
	</v-dialog>
</template>

<script lang="ts" setup>
import { useBuffer } from '@/composables/buffer'
import { useRouteHistory } from '@/composables/useRouteHistory.ts'
import { useDocumentStore } from '@/stores/modules/document'
import { useImageStore } from '@/stores/modules/image'
import { useToastStore } from '@/stores/system'
import { saveAs } from 'file-saver'
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import ImageCard from './ImageCard.vue'
import type { ImageSrc } from './BaseImage.vue'

const { toastError } = useToastStore()

interface Props {
	imageId?: number
	documentId?: number
	imageUrl?: string
	flat?: boolean
	closeBtn?: boolean
	downloadName: string
	maxHeight: number
}

defineEmits(['close'])

const props = defineProps<Props>()
const toggle = ref(false)
const url = ref<ImageSrc>(undefined)
const downloadId = ref(undefined)
const downloadName = ref(undefined)
const flat = ref(false)
const maxHeight = ref(undefined)
const route = useRoute()
const imageStore = useImageStore()
const documentStore = useDocumentStore()
const { buffer2Base64 } = useBuffer()
const routeHistory = useRouteHistory()
watch(toggle, (value) => {
	if (!value) close()
})

onMounted(async () => {
	const state = window.history.state
	const imageId = state?.imageId || props.imageId
	const imageUrl = state?.imageUrl || props.imageUrl
	const documentId = state?.documentId || props.documentId
	downloadName.value =
		state?.downloadName || route.params.name || props.downloadName
	maxHeight.value = state?.maxHeight || props.maxHeight || 600

	flat.value = state?.flat || props.flat || false

	if (imageUrl) {
		url.value = imageUrl
		toggle.value = true
	} else if (imageId) {
		imageStore!
			.loadItem(imageId)
			.then((image) => {
				url.value = image.imageUrl
				toggle.value = true
			})
			.catch(() => {
				url.value = null
				toastError()
			})
	} else if (documentId || route.fullPath.includes('documents')) {
		const id = documentId || route.params.id
		downloadId.value = id

		await documentStore
			.documentBinary({ id, responseType: 'arraybuffer' })
			.then((data) => {
				url.value = buffer2Base64(data.data)
				toggle.value = true
			})
			.catch(() => {
				url.value = null
			})
	} else {
		close()
	}
})

async function save() {
	const fileName = downloadName.value || 'downloaded_image'

	await documentStore
		.documentBinary({ id: downloadId.value, responseType: 'arraybuffer' })
		.then((binary) => {
			const blob = new Blob([binary.data])
			saveAs(blob, fileName)
		})
		.catch((err) => {
			console.error('Error downloading image:', err)
		})
}

function close() {
	routeHistory.navigateToHistoryRoute()
}

function onKeydown(event) {
	if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
		event.preventDefault()
	}

	if (event.code === 'Escape') {
		close()
	}
}

function clickImage(event) {
	console.log(!flat.value)
	flat.value = !flat.value
	toggle.value = true
}
</script>

<style scoped></style>
